import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import {TuiDialogService} from '@taiga-ui/core';
import {Subscription} from 'rxjs';

type ItemFormValue = {
  imageSource: 'upload' | 'url';
  imageUrl: string;
  imageFile: File | null;
  imagePreview: string;

  name: string;
  quantity: number | null;
  price: number | null;
  description: string;
};

@Component({
  selector: 'gatewee-items',
  templateUrl: './gatewee-items.component.html',
  styleUrls: ['./gatewee-items.component.css'],
  standalone: false,
})
export class GateweeItemsComponent implements OnInit, OnDestroy {
  @Input({required: true}) formItems!: FormArray<FormGroup>;

  formSaved: Array<ItemFormValue | null> = [];
  saved: boolean[] = [];

  private sub = new Subscription();

  constructor(
    private fb: FormBuilder,
    private dialogs: TuiDialogService,
    public cdr: ChangeDetectorRef,
  ) {}

  @ViewChild('imagePreview', {static: true})
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private readonly imagePreviewTpl!: TemplateRef<any>;

  openImagePreview(
    src?: string | null,
    description?: string,
    name?: string,
  ): void {
    if (!src) return;

    this.dialogs
      .open(this.imagePreviewTpl, {
        data: {src, description, name},
        closeable: true,
        dismissible: true,
        size: 'l',
      })
      .subscribe();
  }

  ngOnInit(): void {
    this.syncLocalStateWithArray();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get itemsArray(): FormArray<FormGroup> {
    return this.formItems;
  }

  addItemForm(): void {
    const group = this.createItemForm();

    this.itemsArray.push(group);
    this.formSaved.push(null);
    this.saved.push(false);
  }

  removeSingle(i: number): void {
    if (i < 0 || i >= this.itemsArray.length) return;

    this.itemsArray.removeAt(i);
    this.formSaved.splice(i, 1);
    this.saved.splice(i, 1);

    this.formItems.markAsDirty();
    this.formItems.updateValueAndValidity({emitEvent: true});
  }

  removeAll(): void {
    if (!this.itemsArray || this.itemsArray.length === 0) return;

    while (this.itemsArray.length > 0) {
      this.itemsArray.removeAt(0);
    }

    this.formSaved = [];
    this.saved = [];

    this.formItems.markAsDirty();
    this.formItems.updateValueAndValidity({emitEvent: true});
  }

  saveSingle(i: number): void {
    const form = this.itemsArray.at(i);
    if (!form || form.invalid) {
      form?.markAllAsTouched();
      return;
    }

    const raw = form.getRawValue() as ItemFormValue;
    this.formSaved[i] = raw;
    this.saved[i] = true;

    // mantém o valor no próprio FormArray (já está lá), só garante estado
    this.formItems.markAsDirty();
    this.formItems.updateValueAndValidity({emitEvent: true});
  }

  edit(i: number): void {
    if (!this.saved[i]) return;
    this.saved[i] = false;
  }

  // -------------------------
  // Upload helpers
  // -------------------------

  onPickFile(i: number, file: File | null): void {
    const group = this.itemsArray.at(i);
    if (!group) return;

    const fileCtrl = group.get('imageFile') as FormControl<File | null>;
    const previewCtrl = group.get('imagePreview') as FormControl<string>;
    const urlCtrl = group.get('imageUrl') as FormControl<string>;
    const srcCtrl = group.get('imageSource') as FormControl<'upload' | 'url'>;

    srcCtrl.setValue('upload', {emitEvent: false});
    urlCtrl.setValue('', {emitEvent: false});

    fileCtrl.setValue(file);
    fileCtrl.markAsTouched();

    if (!file) {
      previewCtrl.setValue('');
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    previewCtrl.setValue(objectUrl);

    // importante: se trocar o arquivo, invalidar/validar novamente
    fileCtrl.updateValueAndValidity({emitEvent: true});
  }

  setImageSource(i: number, src: 'upload' | 'url'): void {
    const group = this.itemsArray.at(i);
    if (!group) return;

    const srcCtrl = group.get('imageSource') as FormControl<'upload' | 'url'>;
    const fileCtrl = group.get('imageFile') as FormControl<File | null>;
    const previewCtrl = group.get('imagePreview') as FormControl<string>;
    const urlCtrl = group.get('imageUrl') as FormControl<string>;

    srcCtrl.setValue(src);

    if (src === 'url') {
      fileCtrl.setValue(null);
      previewCtrl.setValue('');
      fileCtrl.updateValueAndValidity({emitEvent: false});
    } else {
      urlCtrl.setValue('');
    }
  }

  getFileErrorText(ctrl: AbstractControl | null): string | null {
    if (!ctrl || !ctrl.touched) return null;
    const e = ctrl.errors;
    if (!e) return null;

    if (e['fileType']) return 'Formato inválido. Envie PNG ou JPG.';
    if (e['fileSize']) return 'Arquivo muito grande. Tamanho máximo: 1MB.';
    if (e['fileDim']) return 'Dimensão inválida. A imagem deve ser 400×400.';
    return 'Imagem inválida.';
  }

  // -------------------------
  // Internals
  // -------------------------

  private syncLocalStateWithArray(): void {
    const len = this.itemsArray?.length ?? 0;
    this.formSaved = Array.from({length: len}, (_, i) => {
      const v = this.itemsArray.at(i)?.getRawValue?.();
      return (v ?? null) as ItemFormValue | null;
    });
    this.saved = Array.from({length: len}, () => false);
  }

  private createItemForm(): FormGroup {
    return this.fb.group({
      imageSource: this.fb.control<'upload' | 'url'>('upload', {
        nonNullable: true,
      }),
      imageUrl: this.fb.control<string>('', {nonNullable: true}),
      imageFile: this.fb.control<File | null>(null, {
        validators: [],
        asyncValidators: [this.imageFileValidator({maxMB: 1, w: 400, h: 400})],
        updateOn: 'change',
      }),
      imagePreview: this.fb.control<string>('', {nonNullable: true}),

      name: this.fb.control<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2)],
      }),
      quantity: this.fb.control<number | null>(1, {
        validators: [Validators.required, Validators.min(1)],
      }),
      price: this.fb.control<number | null>(null, {
        validators: [Validators.required, Validators.min(0.01)],
      }),
      description: this.fb.control<string>('', {nonNullable: true}),
    });
  }

  private imageFileValidator(opts: {
    maxMB: number;
    w: number;
    h: number;
  }): AsyncValidatorFn {
    const allowed = ['image/png', 'image/jpeg'];

    return async (
      control: AbstractControl,
    ): Promise<ValidationErrors | null> => {
      const file = control.value as File | null;
      if (!file) return null;

      if (!allowed.includes(file.type)) return {fileType: true};

      const maxBytes = opts.maxMB * 1024 * 1024;
      if (file.size > maxBytes) return {fileSize: true};

      // valida dimensão
      const dimOk = await this.checkImageDimensions(file, opts.w, opts.h);
      if (!dimOk) return {fileDim: true};

      return null;
    };
  }

  private checkImageDimensions(
    file: File,
    w: number,
    h: number,
  ): Promise<boolean> {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        const ok = img.width === w && img.height === h;
        URL.revokeObjectURL(url);
        resolve(ok);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };

      img.src = url;
    });
  }
}
